<template>
  <Head :title="`${t('courses', 2)} - ${institution.abbreviation}`" />
  <s-page-block>
    <s-page-header :heading="t('courses', 2)">
      <template #actions>
        <s-btn
          v-if="can.create"
          :href="route('institutions.courses.create', {institution: institution.id})"
          icon="plus"
          color="primary"
        >
          {{ t('actions.create') }}
        </s-btn>
      </template>
    </s-page-header>

    <s-placeholder v-if="!courses.length" class="mt-4">
      <template #default>
        <p>{{ t('noCourses') }}</p>
      </template>
    </s-placeholder>

    <template v-else>
      <search class="mt-4 mb-4">
        <s-form-inline
          id="searchText"
          icon="magnify"
          class="mb-3"
          :placeholder="t('search.placeholder')"
          :submitLabel="$t('search.label')"
          v-model="form.search"
        />
        <div class="flex justify-end">
          <s-results-count :shown="filteredCourses.length" :total="courses.length" />
        </div>
      </search>

      <s-data-table
        :data="filteredCourses"
        :columns="[
          {key: 'code', label: t('courseCode'), th: {class: 'w-0'}},
          {key: 'name', label: t('name')},
          {key: 'startDate', label: t('startDate')},
          {key: 'endDate', label: t('endDate')},
        ]"
      >
        <template #td:code="{code, id}">
          <s-link :href="route('courses.show', {course: id})" class="link-subtle">
            {{ code }}
          </s-link>
        </template>
        <template #td:name="{name, id}">
          <s-link :href="route('courses.show', {course: id})" class="link-subtle">
            {{ name }}
          </s-link>
        </template>
        <template #td:startDate="{startDate}">
          {{ monthDayYear(startDate) }}
        </template>
        <template #td:endDate="{endDate}">
          {{ monthDayYear(endDate) }}
        </template>
        <template #empty>
          {{ t('noCourses') }}
        </template>
      </s-data-table>
    </template>
  </s-page-block>
</template>

<script setup lang="ts">
import SPageBlock from '../../design-system/SPageBlock.vue';
import SPageHeader from '../../design-system/SPageHeader.vue';
import SPlaceholder from '../../design-system/SPlaceholder.vue';
import {Institution} from '../../types/entities/institution';
import {useI18n} from 'vue-i18n';
import {ref, computed, reactive} from 'vue';
import SFormInline from '../../design-system/SFormInline.vue';
import SLink from '../../components/SLink.vue';
import {Course} from '../../types/entities/course';
import {monthDayYear} from '../../format/dates';
import SDataTable from '../../design-system/SDataTable.vue';
import {route} from 'ziggy-js';
import SResultsCount from '../../design-system/SResultsCount.vue';
import {Head} from '@inertiajs/vue3';
import SBtn from '../../design-system/SBtn.vue';

const {courses} = defineProps<{
  institution: Institution;
  courses: Course[];
  can: {
    create: boolean;
  };
}>();

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const form = reactive({
  search: '',
});

const filteredCourses = computed(() => {
  return courses.filter(
    (course) =>
      course.name.toLowerCase().includes(form.search.toLowerCase()) ||
      course.code.toLowerCase().includes(form.search.toLowerCase())
  );
});
</script>
<i18n>
{
  "en": {
    "actions.create": "Create Course",
    "courseCode": "Course Code",
    "name": "Name",
    "noCourses": "No courses found."
  },
  "fr": {
    "actions.create": "Créer un cours",
    "courseCode": "Code de cours",
    "name": "Nom",
    "noCourses": "Aucun cours trouvé."
  }
}
</i18n>
